import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Download, CheckCircle, AlertCircle, Loader2, Home, FileText, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DownloadPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, ready, downloading, completed, error
  const [downloadData, setDownloadData] = useState(null);
  const [error, setError] = useState(null);

  // Support both download_id and legacy payment_resume_id
  const downloadId = searchParams.get('download_id') || searchParams.get('payment_resume_id') || searchParams.get('id');

  const checkDownloadStatus = async () => {
    try {
      // Use the correct endpoint: /api/resumes/download-status/{download_id}
      const response = await fetch(`${API}/resumes/download-status/${downloadId}`);
      const data = await response.json();
      
      if (response.ok) {
        setDownloadData(data);
        
        if (data.can_download) {
          setStatus('ready');
        } else if (data.requires_payment) {
          setStatus('error');
          setError('Payment not completed. Please complete the payment first.');
        } else if (data.status === 'expired') {
          setStatus('error');
          setError(data.message || 'This download link has expired. Please create a new resume.');
        } else {
          setStatus('error');
          setError('Download not available. Please try again.');
        }
      } else {
        setStatus('error');
        setError(data.detail || 'Could not verify download status. Please contact support.');
      }
    } catch (err) {
      console.error('Error checking download status:', err);
      setStatus('error');
      setError('Failed to check download status. Please try again.');
    }
  };

  useEffect(() => {
    if (downloadId) {
      checkDownloadStatus();
    } else {
      setStatus('error');
      setError('No download ID provided');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadId]);

  const handleDownload = async () => {
    setStatus('downloading');
    
    try {
      // Use direct link approach - works better across all devices including iOS
      // The backend sends Content-Disposition: attachment header
      const downloadUrl = `${API}/resumes/download-resume/${downloadId}?format=pdf`;
      
      // Open in new window/tab - browser will handle the download natively
      // This works on iOS, Android, and desktop
      window.open(downloadUrl, '_blank');
      
      setStatus('completed');
      toast.success('Resume download started!');
      
      // Clear ALL session state after successful download - ensures fresh start next time
      sessionStorage.removeItem('uploadedResumeId');
      sessionStorage.removeItem('selectedTemplateId');
      sessionStorage.removeItem('refinedResumeId');
      sessionStorage.removeItem('refinedContent');
      sessionStorage.removeItem('refinedEditableData');
      sessionStorage.removeItem('downloadId');
      sessionStorage.removeItem('downloadTemplateId');
      sessionStorage.removeItem('convertStep');
      sessionStorage.removeItem('pendingPaymentPlan');
      sessionStorage.removeItem('singleDownloadGranted');
      sessionStorage.removeItem('premiumJustActivated');
      localStorage.removeItem('uploadedResumeId');
      localStorage.removeItem('selectedTemplateId');
      localStorage.removeItem('downloadId');
      localStorage.removeItem('downloadTemplateId');
      localStorage.removeItem('pendingPaymentPlan');
    } catch (err) {
      console.error('Download error:', err);
      setStatus('error');
      setError('Failed to download. Please try again.');
      toast.error('Download failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FileText className="h-6 w-6 text-primary" />
            <span className="font-heading text-xl font-bold text-primary">FixResume24</span>
          </Link>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          {status === 'checking' && (
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Verifying Payment...</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
              </CardContent>
            </Card>
          )}

          {status === 'ready' && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-green-800">Payment Successful!</CardTitle>
                <CardDescription>
                  Your resume is ready to download without watermark.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-8">
                <Button 
                  size="lg" 
                  onClick={handleDownload}
                  className="w-full max-w-xs bg-green-600 hover:bg-green-700"
                  data-testid="download-paid-resume-btn"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Resume (PDF)
                </Button>
                
                {downloadData?.template_id && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Template: {downloadData.template_id}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {status === 'downloading' && (
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Preparing Download...</h2>
                <p className="text-muted-foreground">Your resume is being generated.</p>
              </CardContent>
            </Card>
          )}

          {status === 'completed' && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-green-800">Download Complete!</CardTitle>
                <CardDescription>
                  Your resume has been downloaded successfully.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-8 space-y-4">
                <Button 
                  variant="outline"
                  onClick={handleDownload}
                  className="w-full max-w-xs"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Again
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="w-full max-w-xs"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
              </CardContent>
            </Card>
          )}

          {status === 'error' && (
            <Card className="border-red-200 bg-red-50/50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-red-800">Download Error</CardTitle>
                <CardDescription className="text-red-600">
                  {error || 'Something went wrong'}
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center pb-8 space-y-4">
                <Button 
                  variant="outline"
                  onClick={checkDownloadStatus}
                  className="w-full max-w-xs"
                >
                  Try Again
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/convert')}
                  className="w-full max-w-xs"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
                
                <p className="text-xs text-muted-foreground mt-4">
                  If you completed payment and still see this error,<br />
                  please contact support with your payment ID.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default DownloadPage;
